/* Quality targets, projected from data contracts. Pure — no React, no fetch.
 *
 * **The target is not a setting on this screen, and that is deliberate.** `core.contracts` already
 * holds `min_quality_score` as a term two named parties signed, versioned and immutable once
 * submitted. A separate threshold store on the Scores page would be a second definition of "good
 * enough" — and the second one is the one that drifts, until a dashboard says a dataset is fine
 * while the contract it is bound by says otherwise. So this file only ever *reads* contracts.
 *
 * What that buys, beyond correctness: a breach can name the promise it broke, and the row can name
 * an owner, because a contract carries `producer_subject` — the party who signed up to produce this
 * data to this standard. That is the only ownership signal that exists in this platform today, and
 * it is a real one rather than an invented column.
 */

import type { Contract } from '@/shared/api/generated/contracts';

import type { QualityTarget } from './types';

/** Contracts in force. A draft has not been agreed, a pending one is not yet signed by both sides,
 * and a retired one is a promise that has been withdrawn — none of the three binds anybody, so
 * rendering a target from one would hold a dataset to a standard nobody is currently owed. */
const IN_FORCE = 'active';

/**
 * Dataset id → the target it must meet.
 *
 * **The strictest floor wins when a dataset carries several contracts**, and that is the load-
 * bearing rule. A table feeding both a marketing job at ≥ 80 and a regulatory report at ≥ 95 is
 * bound by both at once; showing the looser one would report a dataset as meeting its target while
 * it is in breach of a promise made to a regulator. Taking the maximum can only ever over-report a
 * breach, which is the safe direction for a control to be wrong in.
 *
 * Ties keep the first contract in the caller's order, which is the server's, so two renders of one
 * estate never attribute a target to a different contract.
 */
export function targetsByDataset(
  contracts: readonly Contract[],
): ReadonlyMap<string, QualityTarget> {
  const byDataset = new Map<string, QualityTarget>();

  for (const contract of contracts) {
    if (contract.state !== IN_FORCE) continue;
    const floor = contract.terms.min_quality_score;
    /* `null` is "this contract promises other things" — an assessment age, a pinned schema version.
     * It is not a floor of zero, and treating it as one would put a target on every dataset that
     * has a contract of any kind. */
    if (floor === null) continue;

    const existing = byDataset.get(contract.dataset_id);
    if (existing !== undefined && existing.minScore >= floor) continue;

    byDataset.set(contract.dataset_id, {
      minScore: floor,
      contractId: contract.id,
      contractName: contract.name,
      severity: contract.breach_severity,
      owner: contract.producer_subject,
      consumer: contract.consumer_subject,
    });
  }

  return byDataset;
}

/**
 * How far a score sits above or below its target. Negative is a breach.
 *
 * `null` when either side is missing, and the two absences are not the same thing: no target means
 * nobody has promised anything, and no score means nobody has measured. Both render as "no target
 * to compare against" rather than as zero slack, because a zero would read as *exactly meeting* a
 * target — the most reassuring possible rendering of "we do not know".
 */
export function slackOf(
  value: number | null,
  target: QualityTarget | null,
): number | null {
  if (value === null || target === null) return null;
  return value - target.minScore;
}
